// @ts-nocheck
import React, { useState } from 'react'
import { Modal, Form, Input, Button, Select, Transfer, message, Spin, Alert, Space, Typography, Tag, Switch, Tooltip } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { gmailService } from '../services/gmailService'
import MaterialIcon from './MaterialIcon'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const { Option } = Select
const { Text } = Typography

interface Customer {
  id: string
  firstName: string
  lastName: string
  email: string
  company?: string
  phone?: string
}

interface ComposeEmailModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [body, setBody] = useState('')
  const [recipientType, setRecipientType] = useState<'all' | 'select'>('select')
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([])
  const [searchText, setSearchText] = useState('')
  const [enableTracking, setEnableTracking] = useState(true)

  // Fetch customers for selection
  const { data: customersData, isLoading: loadingCustomers } = useQuery({
    queryKey: ['bulk-email-customers', searchText],
    queryFn: () => gmailService.getCustomersForBulkEmail(searchText || undefined, 500),
    enabled: visible,
  })

  const customers: Customer[] = customersData?.data || []

  // Send bulk email mutation
  const bulkSendMutation = useMutation({
    mutationFn: (values: any) => {
      return gmailService.bulkSendEmail({
        subject: values.subject,
        body,
        customerIds: recipientType === 'select' ? selectedCustomerIds : undefined,
        sendToAll: recipientType === 'all',
        enableTracking,
      })
    },
    onSuccess: (response) => {
      message.success(
        `Email sent successfully to ${response.data.successful} customer(s)!` +
        (response.data.failed > 0 ? ` (${response.data.failed} failed)` : '')
      )
      form.resetFields()
      setBody('')
      setSelectedCustomerIds([])
      setRecipientType('select')
      onSuccess?.()
      onClose()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to send bulk email')
    },
  })

  const handleSend = async () => {
    try {
      const values = await form.validateFields()

      if (recipientType === 'select' && selectedCustomerIds.length === 0) {
        message.warning('Please select at least one recipient')
        return
      }

      if (!body || body.trim() === '' || body === '<p><br></p>') {
        message.warning('Please enter email content')
        return
      }

      bulkSendMutation.mutate(values)
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const handleRecipientTypeChange = (value: 'all' | 'select') => {
    setRecipientType(value)
    if (value === 'all') {
      setSelectedCustomerIds([])
    }
  }

  // Transfer component data source
  const transferDataSource = customers.map((customer) => ({
    key: customer.id,
    title: `${customer.firstName} ${customer.lastName}`,
    description: customer.email,
    company: customer.company,
  }))

  const filterOption = (inputValue: string, item: any) => {
    return (
      item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
      item.description.toLowerCase().includes(inputValue.toLowerCase()) ||
      (item.company && item.company.toLowerCase().includes(inputValue.toLowerCase()))
    )
  }

  const recipientCount = recipientType === 'all' ? customers.length : selectedCustomerIds.length

  return (
    <Modal
      title={
        <span style={{ fontSize: '18px', fontWeight: 600 }}>
          <MaterialIcon icon="edit" style={{ marginRight: '8px', fontSize: '20px' }} />
          Compose Bulk Email
        </span>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<MaterialIcon icon="send" />}
          loading={bulkSendMutation.isPending}
          onClick={handleSend}
        >
          Send to {recipientCount} {recipientCount === 1 ? 'Customer' : 'Customers'}
        </Button>,
      ]}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Form form={form} layout="vertical">
        {/* Recipient Type Selection */}
        <Form.Item label="Recipients">
          <Select
            value={recipientType}
            onChange={handleRecipientTypeChange}
            style={{ width: '100%' }}
          >
            <Option value="select">
              <Space>
                <MaterialIcon icon="people" />
                Select Specific Customers
              </Space>
            </Option>
            <Option value="all">
              <Space>
                <MaterialIcon icon="groups" />
                All Customers ({customers.length})
              </Space>
            </Option>
          </Select>
        </Form.Item>

        {/* Customer Selection Transfer */}
        {recipientType === 'select' && (
          <Form.Item>
            {loadingCustomers ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
                <p style={{ marginTop: '10px' }}>Loading customers...</p>
              </div>
            ) : (
              <>
                <Transfer
                  dataSource={transferDataSource}
                  titles={['Available Customers', 'Selected Recipients']}
                  targetKeys={selectedCustomerIds}
                  onChange={(keys) => setSelectedCustomerIds(keys as string[])}
                  render={(item) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{item.title}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{item.description}</div>
                      {item.company && (
                        <Tag style={{ fontSize: '11px', marginTop: '4px' }}>{item.company}</Tag>
                      )}
                    </div>
                  )}
                  showSearch
                  filterOption={filterOption}
                  listStyle={{
                    width: 400,
                    height: 350,
                  }}
                  locale={{
                    itemUnit: 'customer',
                    itemsUnit: 'customers',
                    searchPlaceholder: 'Search customers...',
                  }}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                  {selectedCustomerIds.length} customer(s) selected
                </Text>
              </>
            )}
          </Form.Item>
        )}

        {recipientType === 'all' && (
          <Alert
            message={`This email will be sent to all ${customers.length} customers with valid email addresses.`}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Email Tracking Toggle */}
        <Form.Item>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Text>Enable Email Tracking</Text>
              <Tooltip title="Track when customers open emails and click links. Disabling this prevents 'suspicious message' warnings but you won't get open/click statistics.">
                <MaterialIcon icon="info" style={{ fontSize: '16px', color: '#bbb', cursor: 'help' }} />
              </Tooltip>
            </Space>
            <Switch
              checked={enableTracking}
              onChange={setEnableTracking}
              checkedChildren={<MaterialIcon icon="visibility" style={{ fontSize: '12px' }} />}
              unCheckedChildren={<MaterialIcon icon="visibility_off" style={{ fontSize: '12px' }} />}
            />
          </Space>
          {!enableTracking && (
            <Alert
              message="Tracking disabled - emails will appear less suspicious but you won't see open/click stats"
              type="warning"
              showIcon
              style={{ marginTop: '8px' }}
              banner
            />
          )}
        </Form.Item>

        {/* Subject */}
        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: 'Please enter email subject' }]}
        >
          <Input
            placeholder="Email subject"
            prefix={<MaterialIcon icon="subject" style={{ color: '#bbb' }} />}
          />
        </Form.Item>

        {/* Email Body Editor */}
        <Form.Item label="Message">
          <Alert
            message="Personalization Tips"
            description={
              <div>
                Use these placeholders to personalize emails:
                <br />
                <Tag>{'{firstName}'}</Tag> - Customer's first name
                <br />
                <Tag>{'{lastName}'}</Tag> - Customer's last name
                <br />
                <Tag>{'{fullName}'}</Tag> - Customer's full name
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: '12px' }}
            closable
          />
          <ReactQuill
            value={body}
            onChange={setBody}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ color: [] }, { background: [] }],
                ['link'],
                ['clean'],
              ],
            }}
            style={{
              height: '250px',
              marginBottom: '50px',
            }}
            placeholder="Type your message here... Use {firstName}, {lastName}, or {fullName} for personalization"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ComposeEmailModal

